import { isEqual, snakeCase } from 'lodash/fp';
import { ChangeEvent, useEffect, useRef, VFC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import GraphReadOnlyQuery from 'dashboard/components/GraphReadOnlyQuery';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  getTokensToDisplayValue,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { Graph } from 'dashboard/models/graph';
import { VariableValue } from 'dashboard/models/graph-query';
import { Trigger } from 'dashboard/models/trigger';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionRunGraphQueryV1, WorkflowStep } from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

interface ParamsRunGraphQueryV1Props extends ParamsBaseProps {
  accountStatuses: WorkflowActionMeta['account-statuses'];
  editable: boolean;
  errors: WorkflowError[];
  graph: Graph;
  graphQueryTemplates: WorkflowActionMeta['graph-query-templates'];
  pathToAction: WorkflowStep[];
  params: WorkflowActionRunGraphQueryV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionRunGraphQueryV1['params']) => void;
}

const getVariableMap = (
  inputSchema: Record<string, string>,
  queryInputs: Record<string, string>,
) => {
  return Object.keys(inputSchema).reduce((acc: Record<string, string>, variableName: string) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    acc[variableName] = queryInputs[variableName] || null;
    return acc;
  }, {});
};

const ParamsRunGraphQueryV1: VFC<ParamsRunGraphQueryV1Props> = ({
  accountStatuses,
  editable,
  errors,
  graph,
  graphQueryTemplates,
  pathToAction,
  params,
  step,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const hasGraphQueryTemplatePerm = usePermission(Permission.GraphQueryTemplateView);
  const showTimeout = useFeatureFlag('dashboard:graph-run-graph-query-step-timeout-param');
  const [showEditPanel, { toggle: toggleEditPanel, disable: hideEditPanel }] = useToggle(false);
  const initialParams = useRef<Record<string, string>>({});
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);
  const graphQueryTemplateOptions: { label: string; value: string }[] = graphQueryTemplates.map(
    (gqt) => {
      return { label: gqt.attributes.name, value: gqt.id };
    },
  );

  const selectedGraphQueryTemplate = graphQueryTemplates.find(
    (gqt) => gqt.id === params['graph-query-template-id'],
  );

  useEffect(() => {
    if (selectedGraphQueryTemplate) {
      initialParams.current = getVariableMap(
        selectedGraphQueryTemplate.attributes['input-schema'],
        params['query-inputs'],
      );

      if (
        'account-id' in selectedGraphQueryTemplate.attributes['input-schema'] &&
        !('account-id' in params['query-inputs'])
      ) {
        // pre-fill the `account-id` query parameter, if it exists, with the first usable `account` object found in the workflow
        const filteredObjects = filterUsableObjects({
          usableObjects,
          options: {
            permittedObjects: [WorkflowFieldObjectKey.Account],
            excludeNonPersonaObjects: true,
          },
        });
        if (filteredObjects.length) {
          initialParams.current['account-id'] = `${filteredObjects[0].value}.id`;
        }
      }

      if (!isEqual(initialParams.current, params['query-inputs'])) {
        updateActionParams({
          ...params,
          'query-inputs': initialParams.current,
        });
      }
    }
  }, [
    usableObjects,
    params,
    pathToAction,
    selectedGraphQueryTemplate,
    trigger,
    updateActionParams,
    step,
  ]);

  const handleGraphQueryTemplateChange = (value: string) => {
    if (showEditPanel) {
      hideEditPanel();
    }

    const selectedGraphQueryTemplateOption = graphQueryTemplateOptions.find(
      (option) => option.value === value,
    );

    let selectedGraphQueryTemplate;
    if (selectedGraphQueryTemplateOption) {
      selectedGraphQueryTemplate = graphQueryTemplates.find((gqt) => gqt.id === value);
    }

    if (selectedGraphQueryTemplate) {
      initialParams.current = getVariableMap(
        selectedGraphQueryTemplate.attributes['input-schema'],
        params['query-inputs'],
      );

      if (
        'account-id' in selectedGraphQueryTemplate.attributes['input-schema'] &&
        !('account-id' in params['query-inputs'])
      ) {
        // pre-fill the `account-id` query parameter, if it exists, with the first usable `account` object found in the workflow
        const filteredObjects = filterUsableObjects({
          usableObjects,
          options: {
            permittedObjects: [WorkflowFieldObjectKey.Account],
            excludeNonPersonaObjects: true,
          },
        });
        if (filteredObjects.length) {
          initialParams.current['account-id'] = `${filteredObjects[0].value}.id`;
        }
      }
    }

    updateStep({
      ...step,
      name: selectedGraphQueryTemplateOption?.label
        ? `Run ${selectedGraphQueryTemplateOption.label}`
        : ' Graph Query',
      action: {
        ...(step.action as WorkflowActionRunGraphQueryV1),
        params: {
          ...params,
          'graph-query-template-id': selectedGraphQueryTemplateOption?.value,
          'query-inputs': selectedGraphQueryTemplate ? initialParams.current : {},
        },
      } as WorkflowActionRunGraphQueryV1,
    });
  };

  const handleEditVariableMap = (
    value: WorkflowActionRunGraphQueryV1['params']['query-inputs'],
  ) => {
    updateActionParams({
      ...params,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'graph-query-template-id': selectedGraphQueryTemplate?.id,
      'query-inputs': value || {},
    });
  };

  const onChangeTimeout = (event: ChangeEvent<HTMLInputElement>) => {
    const timeout = parseInt(event.target.value);

    updateActionParams({
      ...params,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timeout: isNaN(timeout) ? null : timeout,
    });
  };

  // fill in the variable values in the readonly query to be the graph query inputs
  if (selectedGraphQueryTemplate) {
    const queryTemplateVariables: VariableValue[] = Object.entries(params['query-inputs']).map(
      ([fieldKey, fieldValue]) => {
        return {
          name: snakeCase(fieldKey),
          type: 'string',
          value: fieldValue,
        };
      },
    );
    selectedGraphQueryTemplate.attributes.payload.variables = queryTemplateVariables;
  }

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Graph Query Template'
        htmlFor='params-run-graph-query__graph-query-template-input'
        helpText={
          hasGraphQueryTemplatePerm
            ? undefined
            : 'Your role does not have permissions to view graph query templates.'
        }
      />
      <ReactSelect
        value={selectedGraphQueryTemplate?.id}
        options={graphQueryTemplateOptions}
        onChange={handleGraphQueryTemplateChange}
        disabled={!editable || !hasGraphQueryTemplatePerm}
        placeholder={hasGraphQueryTemplatePerm ? undefined : 'RESTRICTED'}
        id='params-run-graph-query__graph-query-template'
        hasError={hasErrorFor('graph-query-template-id')}
      />
      {renderErrorMessageFor('graph-query-template-id')}
      <Spacer height={2} />
      <Accordion title='Query Inputs' defaultExpanded>
        {showEditPanel && (
          <FieldMappingsPanel
            title='Query inputs'
            usableObjects={usableObjects}
            fieldMappings={params['query-inputs'] ?? {}}
            onOutsideClick={hideEditPanel}
            onUpdateFieldMappings={handleEditVariableMap}
            removable={false}
          />
        )}
        {selectedGraphQueryTemplate && [
          <Spacer key={`graph-readonly-space-${selectedGraphQueryTemplate.id}`} height={1} />,
          <GraphReadOnlyQuery
            accountStatuses={accountStatuses}
            key={`graph-readonly-${selectedGraphQueryTemplate.id}`}
            graph={graph}
            query={selectedGraphQueryTemplate}
          />,
        ]}
        <Spacer height={2} />
        <FieldsList
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          editable={
            editable &&
            selectedGraphQueryTemplate &&
            Object.keys(selectedGraphQueryTemplate.attributes['input-schema']).length > 0
          }
          fields={params['query-inputs']}
          fieldKey='query-inputs'
          errors={errors}
          toggleEditPanel={toggleEditPanel}
          tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
          placeholderEmptyText={
            !selectedGraphQueryTemplate
              ? 'Please select a Graph Query Template'
              : Object.keys(selectedGraphQueryTemplate.attributes['input-schema']).length === 0
                ? 'Graph Query Template has no query inputs'
                : 'No fields selected'
          }
        />
      </Accordion>
      {renderErrorMessageFor('query-inputs')}
      <Spacer height={2} />
      {showTimeout && (
        <>
          <ParamLabel labelText='Timeout' htmlFor='params-run-graph-query__timeout' />
          <Paragraph size='sm'>
            {' '}
            Number of milliseconds to wait for the Graph query to complete before erroring
          </Paragraph>

          <Input
            id='params-run-graph-query__timeout'
            type='number'
            value={params['timeout'] ?? ''}
            hasError={hasErrorFor('timeout')}
            aria-invalid={hasErrorFor('timeout')}
            onChange={onChangeTimeout}
          />
          {renderErrorMessageFor('timeout')}
          <Spacer height={1} />
        </>
      )}
      <Checkbox
        checked={params['generate-snapshot'] ?? false}
        label='Generate Snapshot of Query Result'
        onChange={({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
          updateActionParams({
            ...params,
            'generate-snapshot': checked,
          });
        }}
      />
      <Spacer height={2} />
      <Checkbox
        checked={params['async'] ?? false}
        label='Run Query Asynchronously'
        onChange={({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
          updateActionParams({
            ...params,
            async: checked,
          });
        }}
      />
    </>
  );
};

export default ParamsRunGraphQueryV1;
